import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as api from "../../api";
import { ReactComponent as CloseIcon } from "../../assets/images/cross.svg";
import { ReactComponent as ErrorIcon } from "../../assets/images/input-error.svg";
import RegistrationHeader from "../Utils/RegistrationHeader";
import OtpVerification from "./OtpVerification";
import AuthModal from "./AuthModal";

const Login = ({
  close = () => { },
  handleLogin = () => { },
  handleRegistrationPopup = () => { },
  registrationHeader = {},
  newUser = {},
  setNewUser = () => { },
}) => {
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [otpInitialCooldown, setOtpInitialCooldown] = useState(0);
  const [otpInitialError, setOtpInitialError] = useState("");

  const schema = yup.object({
    email: yup.string().email("Invalid Email").required("Email is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const onSubmit = (data) => {
    setFormError("");
    setIsSubmitting(true);
    api.auth
      .login(data.email)
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          setLoginEmail(response.data?.email || data.email);
          setOtpInitialCooldown(60);
          setOtpStep(true);
        }
      })
      .catch((error) => {
        const errData = error.response?.data;
        if (errData?.code === "otp_throttled") {
          const retryAfter = parseInt(errData.retryAfter, 10) || 60;
          setLoginEmail(data.email);
          setOtpInitialCooldown(retryAfter);
          setOtpInitialError(errData.detail || "Too many OTP requests. Please try again later.");
          setOtpStep(true);
        } else if (error.response?.status === 404) {
          setNewUser({
            message: "No account found for ",
            email: data.email,
            messageLine2: ". Please register to continue.",
          });
          close();
          handleRegistrationPopup("register", true);
        } else {
          setFormError(
            errData?.detail ||
              errData?.nonFieldErrors?.[0] ||
              "Something went wrong. Please try again."
          );
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  if (otpStep) {
    return (
      <OtpVerification
        email={loginEmail}
        close={close}
        initialCooldown={otpInitialCooldown}
        initialError={otpInitialError}
        onVerified={(responseData) => {
          handleLogin(
            responseData.firstName,
            responseData.lastName,
            responseData.gender,
            responseData.email,
            responseData.mobileNumber,
            responseData.pk
          );
          setNewUser({});
          close();
        }}
      />
    );
  }

  return (
    <AuthModal onClose={close}>
      <div className="popup registration-popup confirmation-modal__popup">
        <CloseIcon className="popup__close" onClick={() => close()} />
        <div className="popup__body popup__body--registration">
          {Object.keys(registrationHeader).length !== 0 && (
            <React.Fragment>
              <RegistrationHeader registrationHeader={registrationHeader} />
              <p className="registration-form__lock-paragraph">
                Your passes are locked successfuly
              </p>
              <p className="registration-form__lock-warning">
                Login to be able to checkout
              </p>
            </React.Fragment>
          )}
          {Object.keys(newUser).length !== 0 && (
            <p className="registration-form__new-user-message">
              {newUser.message}
              <span className="registration-form__new-user-email">
                {newUser.email}
              </span>
              {newUser.messageLine2}
            </p>
          )}
          <form
            className={
              "registration-form registration-form--login" +
              (Object.keys(registrationHeader).length !== 0
                ? " registration-form--timed"
                : "")
            }
            onSubmit={handleSubmit(onSubmit)}
          >
            <h3 className="registration-form__title">Login</h3>
            {formError && (
              <p className="error-message registration-form__form-error">
                {formError}
              </p>
            )}
            <div className="registration-form__input-group">
              <label
                htmlFor="email"
                className="registration-form__label registration-form__label--required"
              >
                Email
              </label>
              <div className="registration-form__input-box">
                <input
                  type="text"
                  id="email"
                  placeholder="Email"
                  {...register("email")}
                  className={
                    "registration-form__input" +
                    (errors.email ? " registration-form__input--error" : "")
                  }
                />
                {errors.email && (
                  <ErrorIcon className="registration-form__input-icon" />
                )}
              </div>
              {errors?.email && (
                <p className="error-message registration-form__input-error-message">
                  {errors?.email?.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="btn btn--primary registration-form__btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Code"}
            </button>
            <p className="registration-form__redirect registration-form__redirect--paragraph">
              Don't have an account?
              <span
                className="registration-form__redirect--btn"
                onClick={() => {
                  close();
                  handleRegistrationPopup("register", true);
                }}
              >
                Register
              </span>
            </p>
          </form>
        </div>
      </div>
    </AuthModal>
  );
};

export default Login;
